import { CHAINS, CURRENT_CHAIN, LHYPE_ACCOUNTANT_ADDRESS, LHYPE_ADDRESS } from '@hyperswap/addresses'
import { Currency } from '@uniswap/sdk-core'
import { ButtonPrimary } from 'components/Button'
import { AutoColumn } from 'components/Column'
import CurrencyLogo from 'components/CurrencyLogo'
import IntroCard from 'components/IntroCard'
import { RowBetween } from 'components/Row'
import { SkeletonText } from 'components/Skeleton'
import { API_NUCLEUS_BASE_URL, LINKS } from 'constants/links'
import { useActiveWeb3React } from 'hooks'
import { useToken } from 'hooks/Tokens'
import React, { useContext, useState } from 'react'
import { usePrivy } from '@privy-io/react-auth'
import styled, { ThemeContext } from 'styled-components'
import { TYPE } from 'theme'
import Stake from './Stake'
import Unstake from './Unstake'
import { useTokenBalance } from 'state/wallet/hooks'
import { roundToDecimalPlace } from 'utils/data'
import { getContract } from 'utils'

import { formatUnits } from 'ethers/lib/utils'

import LHYPE_ABI from '../../../abis/looped/LHYPE.json'
import LHYPE_ACCOUNTANT_ABI from '../../../abis/looped/AccountantWithRateProviders.json'
import axios from 'axios'
import Tag from 'components/Tag'
import TagButton from 'components/TagButton'

export const Tabs = styled.div`
  ${({ theme }) => theme.flexRowNoWrap}
  align-items: center;
  border-radius: 14px;
  display: flex;
  gap: 2px;
`

export const StyledTabButton = styled.button<{ isActive?: boolean }>`
  ${({ theme }) => theme.flexRowNoWrap}
  border: none;
  align-items: center;
  justify-content: center;
  outline: none;
  cursor: pointer;
  text-decoration: none;
  color: white;
  padding: 8px 16px;
  border-radius: 14px;
  font-weight: 600;
  font-size: 14px;
  transition: all 300ms;
  background: ${({ isActive }) => (isActive ? '#4AFC38' : 'transparent')};
  color: ${({ isActive }) => (isActive ? 'black' : 'white')};

  &:hover {
    color: #4afc38;
  }

  ${({ isActive }) =>
    isActive &&
    `
    &:hover {
      color: black !important;
    }
  `}
`

const ConnectWalletContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: auto;
  min-height: 25vh;
  background: ${({ theme }) => theme.bg0};
  padding: 8px;
  border-radius: 14px;
  width: 100%;

  div {
    max-width: 300px;
  }
`
const TitleRow = styled(RowBetween)`
  color: ${({ theme }) => theme.text2};
  ${({ theme }) => theme.mediaWidth.upToSmall`
    flex-wrap: wrap;
    gap: 12px;
    width: 100%;
    flex-direction: column-reverse;
  `};
`

const Links = styled.div`
  display: flex;
  flex-direction: row;
  margin-top: 6px;
  gap: 2px;
  a {
    padding: 2px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 300ms ease;

    &:hover {
      background: #81fce0;
      svg {
        color: black;
      }
    }
  }
  svg {
    width: 16px;
    height: 16px;
    color: white;
  }
`

const GridContainer = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: 1fr;

  @media (min-width: 768px) {
    grid-template-columns: calc(55% - 8px) calc(45% - 8px);
  }
`

const EntriesContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(0px, 1fr));
  gap: 15px 0.5rem;
  padding: 0;
  margin-top: 6px;
`

const SubContainer = styled.div<{ bg1: boolean }>`
  background: ${({ theme }) => theme.bg0};
  border-radius: 14px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
`
const SubContainerHeader = styled.div`
  border-radius: 14px;
  background: ${({ theme }) => theme.bg1};
  padding: 20px;
  p {
    color: ${({ theme }) => theme.text2};
    font-size: 12px;
    margin-bottom: 0;
  }
`

const SubContainerBody = styled.div`
  padding: 20px;
  background: ${({ theme }) => theme.bg0};
`

const HistoryTableContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;
  gap: 12px;
`

const HistoryItem = styled.div`
  padding: 12px;
  display: flex;
  background: ${({ theme }) => theme.bg1};
  border-radius: 14px;
  flex-direction: column;
  gap: 4px;
`

function formatTimestamp(timestamp: number) {
  const date = new Date(timestamp * 1000)
  const options: Intl.DateTimeFormatOptions = {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false, // 24-hour format
  }

  return new Intl.DateTimeFormat('en-US', options).format(date).replace(',', '')
}

const EarnLHYPE = () => {
  const { account, chainId, library } = useActiveWeb3React()
  const { login } = usePrivy()
  const theme = useContext(ThemeContext)
  const [tab, setTab] = React.useState<0 | 1>(0)
  const lHYPEToken = useToken(LHYPE_ADDRESS)
  const [historyWithdraw, setHistoryWithdraw] = React.useState([])

  const [data, setData] = useState<any>({
    ra: null,
    totalHYPEStaked: null,
    rateStHYPEToHYPE: 1,
  })
  const [loading, setLoading] = useState<boolean>(true)
  const lToken = useToken(LHYPE_ADDRESS)
  const tokenBalance = useTokenBalance(account ?? undefined, lToken ?? undefined)

  React.useEffect(() => {
    async function fetchData() {
      try {
        if (library && account) {
          const lHYPEContract = getContract(LHYPE_ADDRESS, LHYPE_ABI.abi, library, account)
          const accountantContract = getContract(LHYPE_ACCOUNTANT_ADDRESS, LHYPE_ACCOUNTANT_ABI.abi, library, account)
          const totalSupplyRaw = await lHYPEContract.totalSupply()
          const totalSupply = formatUnits(totalSupplyRaw, 18)

          const rateRaw = await accountantContract.getRate()
          const rate = formatUnits(rateRaw, 18)

          const tvl = parseFloat(totalSupply) * parseFloat(rate)
          setData({
            ra: null,
            tvl: tvl,
            rate: rate,
          })

          setLoading(false)
        }
      } catch (error) {
        console.error(error)
        setLoading(false)
      }
    }

    if (!chainId || !library || !account) return
    if (loading) {
      fetchData()
    }
  }, [account, chainId, library, loading])

  React.useEffect(() => {
    const fetchData = async () => {
      if (library && account && loading) {
        const response = await axios.get(`${API_NUCLEUS_BASE_URL}`, {
          params: {
            chainId: CURRENT_CHAIN,
            vaultAddress: LHYPE_ADDRESS,
            status: 'all',
            user: account,
            all: false,
            page: 1,
            limit: 200,
          },
        })
        setHistoryWithdraw(response.data?.data?.reverse())
      }
    }
    fetchData()
  }, [library, account, loading])

  const setRefresh = () => {
    setLoading(true)
  }

  const randomKey = Math.random() * 99999
  return (
    <>
      <IntroCard variant={4} Key="LHYPE">
        <h1>Stake HYPE, Earn LHYPE</h1>
        <p>Powered by loopedHYPE</p>
        <Links>
          <a href="https://loopedhype.com/" target="_blank" rel="noreferrer" aria-label="Thunderhead Website">
            {LINKS['landingPage'].logo}
          </a>
          <a href="https://x.com/looped_hype" target="_blank" rel="noreferrer" aria-label="Thunderhead Twitter">
            {LINKS['twitter'].logo}
          </a>
        </Links>
      </IntroCard>
      <AutoColumn gap="lg" justify="center">
        <AutoColumn gap="lg" style={{ width: '100%' }}>
          <TitleRow style={{ marginTop: '1rem' }} padding={'0'}>
            <TYPE.mediumHeader>{'Staking Pool Overview'}</TYPE.mediumHeader>
          </TitleRow>
          {!account ? (
            <ConnectWalletContainer>
              <div>
                <TYPE.mediumHeader textAlign="center" color={theme.text3}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="48"
                    height="48"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="icon icon-tabler icons-tabler-outline icon-tabler-tax"
                    style={{ marginBottom: '.5rem' }}
                  >
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <path d="M8.487 21h7.026a4 4 0 0 0 3.808 -5.224l-1.706 -5.306a5 5 0 0 0 -4.76 -3.47h-1.71a5 5 0 0 0 -4.76 3.47l-1.706 5.306a4 4 0 0 0 3.808 5.224" />
                    <path d="M15 3q -1 4 -3 4t -3 -4z" />
                    <path d="M14 11h-2.5a1.5 1.5 0 0 0 0 3h1a1.5 1.5 0 0 1 0 3h-2.5" />
                    <path d="M12 10v1" />
                    <path d="M12 17v1" />
                  </svg>

                  <div>{'Connect your wallet to stake HYPE and earn LHYPE.'}</div>
                </TYPE.mediumHeader>
                <ButtonPrimary
                  style={{ marginTop: '2em', padding: '8px 16px' }}
                  onClick={() =>
                    login({
                      loginMethods: ['wallet'],
                      walletChainType: 'ethereum-only',
                      disableSignup: false,
                    })
                  }
                >
                  Connect a wallet
                </ButtonPrimary>
              </div>
            </ConnectWalletContainer>
          ) : (
            <GridContainer>
              <div>
                <SubContainer bg1={false}>
                  <>
                    <SubContainerHeader>
                      <div style={{ display: 'flex', fontSize: '16px', marginBottom: '12px' }}>
                        <CurrencyLogo currency={lHYPEToken as Currency} size={'42px'} />
                        <h2 style={{ fontSize: '16px', marginLeft: '12px' }}>Earn LHYPE</h2>
                      </div>

                      <EntriesContainer>
                        <span style={{ fontSize: '14px', fontWeight: 800 }}>Annual Protocol Yield</span>
                        <span style={{ fontSize: '14px', fontWeight: 800 }}>Total HYPE Staked</span>
                      </EntriesContainer>
                      <EntriesContainer>
                        <span style={{ fontSize: '14px', fontWeight: 400, height: '16px' }}>
                          Target APY: ≈10%
                          {/*  {loading || !data?.ra ? (
                          <SkeletonText width="40%" height="100%" />
                        ) : (
                          `${roundToDecimalPlace(data?.ra, '3')}%`
                        )} */}
                        </span>
                        <span style={{ fontSize: '14px', fontWeight: 400, height: '16px' }}>
                          {loading || !data?.tvl ? (
                            <SkeletonText width="30%" height="100%" />
                          ) : (
                            `${roundToDecimalPlace(data?.tvl, '3')} HYPE`
                          )}
                        </span>
                      </EntriesContainer>

                      <EntriesContainer>
                        <span style={{ fontSize: '14px', fontWeight: 800 }}>Exchange Rate</span>
                        <span style={{ fontSize: '14px', fontWeight: 800 }}>Max deposit per wallet</span>
                      </EntriesContainer>
                      <EntriesContainer>
                        <span style={{ fontSize: '14px', fontWeight: 400, height: '16px' }}>
                          {!data?.rate || loading ? (
                            <SkeletonText width="30%" height="100%" />
                          ) : (
                            `1 LHYPE = ${roundToDecimalPlace(`${data?.rate}`, '6')} HYPE`
                          )}
                        </span>
                        <span style={{ fontSize: '14px', fontWeight: 400, height: '16px' }}>60 000 HYPE</span>
                      </EntriesContainer>
                      <p style={{ marginBottom: '0' }}>
                        <u>Disclaimer</u>: The staking services provided on this page are powered by loopedHYPE and are
                        not directly developed or maintained by HyperSwap Exchange. While we aim to collaborate with
                        reputable partners, please note that HyperSwap Exchange does not control or manage the
                        underlying smart contracts associated with these staking operations.
                      </p>
                    </SubContainerHeader>

                    <SubContainerBody>
                      <div style={{ marginTop: '16px' }}>
                        <EntriesContainer>
                          <span style={{ fontWeight: 800, fontSize: '16px' }}>Staked</span>
                        </EntriesContainer>
                        <EntriesContainer>
                          {loading ? (
                            <span style={{ fontSize: '20px', fontWeight: 600, color: '#4afc38', height: '20px' }}>
                              <SkeletonText width="55%" height="100%" />
                            </span>
                          ) : (
                            <span style={{ fontSize: '20px', fontWeight: 600, color: '#4afc38' }}>
                              {roundToDecimalPlace(`${tokenBalance?.toExact()}`, '5')} LHYPE
                            </span>
                          )}
                        </EntriesContainer>
                      </div>
                    </SubContainerBody>

                    {historyWithdraw.length > 0 && (
                      <>
                        <span style={{ fontWeight: 800, fontSize: '16px', paddingLeft: '20px' }}>Withdraw History</span>
                        <HistoryTableContainer>
                          {historyWithdraw.map((withdraw: any) => {
                            const hash = withdraw.ending_transaction_hash

                            return (
                              <HistoryItem key={withdraw?.id}>
                                <div style={{ display: 'flex', gap: '4px' }}>
                                  <Tag variant={withdraw.status !== 'fulfilled' ? 1 : 2}>{withdraw.status}</Tag>
                                  <Tag variant={1}>{formatTimestamp(withdraw.created_timestamp)}</Tag>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <span style={{ fontSize: '14px' }}>
                                    Withdraw {roundToDecimalPlace(formatUnits(withdraw.amount, 18), '2')} LHYPE
                                  </span>
                                  {hash && withdraw.status === 'fulfilled' && (
                                    <TagButton
                                      onClick={() =>
                                        window.open(
                                          `${CHAINS[CURRENT_CHAIN].blockExplorerUrls[0]}/tx/${hash}`,
                                          '_blank'
                                        )
                                      }
                                    >
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="14"
                                        height="14"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="1"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="icon icon-tabler icons-tabler-outline icon-tabler-external-link"
                                      >
                                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                        <path d="M12 6h-6a2 2 0 0 0 -2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-6" />
                                        <path d="M11 13l9 -9" />
                                        <path d="M15 4h5v5" />
                                      </svg>
                                      Explorer
                                    </TagButton>
                                  )}
                                </div>
                              </HistoryItem>
                            )
                          })}
                        </HistoryTableContainer>
                      </>
                    )}
                  </>
                </SubContainer>
              </div>
              <div>
                <SubContainer bg1={false}>
                  <SubContainerHeader>
                    <Tabs>
                      <StyledTabButton isActive={tab === 0} onClick={() => setTab(0)}>
                        Stake
                      </StyledTabButton>
                      <StyledTabButton isActive={tab === 1} onClick={() => setTab(1)}>
                        Unstake
                      </StyledTabButton>
                    </Tabs>

                    {tab === 0 ? (
                      <>
                        <p
                          style={{
                            fontSize: '16px',
                            color: 'white',
                            display: 'flex',
                            gap: '6px',
                            fontWeight: 600,
                            alignItems: 'center',
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="icon icon-tabler icons-tabler-outline icon-tabler-speakerphone"
                          >
                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                            <path d="M18 8a3 3 0 0 1 0 6" />
                            <path d="M10 8v11a1 1 0 0 1 -1 1h-1a1 1 0 0 1 -1 -1v-5" />
                            <path d="M12 8h0l4.524 -3.77a.9 .9 0 0 1 1.476 .692v12.156a.9 .9 0 0 1 -1.476 .692l-4.524 -3.77h-8a1 1 0 0 1 -1 -1v-4a1 1 0 0 1 1 -1h8" />
                          </svg>
                          Phase 2 is live!
                        </p>

                        <p style={{ color: 'white', marginBottom: '0', fontSize: '14px' }}>
                          Please enter the amount of HYPE Tokens you wish to stake.
                        </p>
                      </>
                    ) : (
                      <>
                        <p>
                          <u>Unstaking Process & Delay Disclaimer</u>: Before proceeding, please note that the unstaking
                          process is asynchronous. Once you submit your request, the order will be queued but may take
                          up to <b>24 hours</b> to be fully executed, depending on when the solver runs.
                          <br />
                          <br />
                          After the process is complete, you will receive <b>stHYPE</b> tokens in your wallet. If the
                          solver does not execute the order within this timeframe, it may expire.
                          <br />
                          <br />
                          Make sure you’re aware of this delay before confirming your unstake request.
                        </p>
                        <p style={{ color: 'white', marginBottom: '0', fontSize: '14px' }}>
                          Please enter the amount of LHYPE Tokens you wish to unstake.
                        </p>
                      </>
                    )}
                  </SubContainerHeader>
                  {tab === 0 ? (
                    <Stake setRefresh={setRefresh} key={randomKey} />
                  ) : (
                    <Unstake setRefresh={setRefresh} key={randomKey} />
                  )}
                </SubContainer>
              </div>
            </GridContainer>
          )}
        </AutoColumn>
      </AutoColumn>
    </>
  )
}
export default EarnLHYPE
